import Vue from "vue";
import i18n from "@/plugins/i18n";
import router from "@/router";

export default function ({ globals }) {
	return {
		namespaced: true,
		state: {
			name: "",
			userName: "",
			password: "",
			phoneNumber: "",
			workspaceName: "",
			emailProvided: "",
			ssoCode: null,
			oAuthState: null,
			invitationHash: null,
			registrationEmailConfirmation: false,
			registrationEmailCode: "",
			ssoSubId: "",
			userWasInvited: false,
			invitationData: null,
			loadingConfirmationStatus: true,
			inlineErrors: {
				userNameNotValidError: null,
				phoneNumberNotValidError: null,
				workspaceNameNotValidError: null,
			},
			registrationToken: null,
		},
		mutations: {
			SET_NAME(state, value) {
				state.name = value;
			},
			SET_USERNAME(state, value) {
				state.userName = value ? value.replace(/\s/g, "") : value;
			},
			SET_PASSWORD(state, value) {
				state.password = value ? value.replace(/\s/g, "") : value;
			},
			SET_PHONENUMBER(state, value) {
				state.phoneNumber = value ? value.replace(/\s/g, "") : value;
			},
			SET_WORKSPACENAME(state, value) {
				state.workspaceName = value;
			},
			SET_EMAIL_PROVIDED(state, value) {
				state.emailProvided = value;
			},
			SET_REGISTRATION_EMAIL_CONFIRMATION(state, value) {
				state.registrationEmailConfirmation = value;
			},
			SET_LOADING_CONFIRMATION_STATUS(state, value) {
				state.loadingConfirmationStatus = value;
			},
			SET_SSO_SUB_ID(state, value) {
				state.ssoSubId = value;
			},
			SET_SSO_CODE(state, value) {
				state.ssoCode = value;
			},
			SET_OAUTH_STATE(state, value) {
				state.oAuthState = value;
			},
			SET_REGISTRATION_TOKEN(state, value) {
				state.registrationToken = value;
			},
			SET_INVITATION_HASH(state, value) {
				state.invitationHash = value;
			},
			SET_USER_WAS_INVITED(state, value) {
				state.userWasInvited = value;
			},
			SET_INVITATION_DATA(state, value) {
				state.invitationData = value;
			},
			SET_INVITATION_STATUS(state, { workspaceId, value }) {
				const invitation = state?.invitationData?.invitations?.find(inv => inv.workspaceId === workspaceId);
				if (!invitation) {
					return;
				}
				invitation.status = value;
			},
			SET_REGISTRATION_EMAIL_CODE(state, value) {
				state.registrationEmailCode = value;
			},
			SET_INLINE_ERROR(state, { key, value }) {
				Vue.set(state.inlineErrors, key, value);
			},
		},
		getters: {
			allInvitationsAreDeclined(state) {
				return state.invitationData?.invitations?.every(invitation => invitation.status === "declined");
			},
			allInvitationsDecisionsAreMade(state) {
				return state.invitationData?.invitations?.every(invitation => invitation.status !== "pending");
			},
		},
		actions: {
			async checkUserNameAvailability({ state, commit, dispatch }) {
				try {
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const userNameAvailabilityResponse = await fetch(`${globalConfig.backends.users}/availability/username`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							userName: state.userName,
						}),
					});

					const { result, error = null } = await userNameAvailabilityResponse.json();

					if (error) throw new Error(error.code);

					if (!result.userName) {
						throw new Error("usernameTaken");
					}

					commit("SET_INLINE_ERROR", { key: "userNameNotValidError", value: null });
				} catch (error) {
					const translationExists = i18n.te(`setUpAccountUserComponent.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";

					if (error.message === "usernameTaken") {
						commit("SET_INLINE_ERROR", { key: "userNameNotValidError", value: i18n.t("rules.usernameTaken") });
					}
					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`setUpAccountUserComponent.errorMessages.${code}`),
						target: {
							type: error.message === "usernameTaken" ? "inline" : "flashToast",
							timeout: 4000,
						},
					}, { root: true });
				}
			},
			async confirmRegistrationEmail({ state, commit, dispatch }) {
				try {
					commit("SET_LOADING_CONFIRMATION_STATUS", true);
					if (!state.registrationEmailCode) {
						commit("SET_REGISTRATION_EMAIL_CONFIRMATION", false);
						commit("SET_LOADING_CONFIRMATION_STATUS", false);
						return;
					}
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const confirmEmailRequest = await fetch(`${globalConfig.backends.users}/register/${state.registrationEmailCode}/confirm`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
					});

					const { result, error = null } = await confirmEmailRequest.json();
					if (error) throw new Error(error.code);

					this._vm.$chamaileon.pushToGtmDataLayer({
						event: "confirmEmail",
						email: result.email,
					});
					commit("SET_REGISTRATION_EMAIL_CONFIRMATION", true);
					commit("SET_LOADING_CONFIRMATION_STATUS", false);
					if (result.invitationData) {
						commit("SET_USER_WAS_INVITED", true);
						commit("SET_INVITATION_DATA", result.invitationData);
					}
				} catch (error) {
					commit("SET_REGISTRATION_EMAIL_CONFIRMATION", false);
					commit("SET_LOADING_CONFIRMATION_STATUS", false);

					const translationExists = i18n.te(`confirmRegistrationEmailView.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";

					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`confirmRegistrationEmailView.errorMessages.${code}`),
					}, { root: true });
				}
			},
			async ssoGetTokensForRegistration({ state, commit, dispatch }) {
				try {
					if (!state.oAuthState) {
						throw new Error("State does not exist");
					}
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const response = await fetch(`${globalConfig.backends.users}/sso-gettoken/`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							code: state.ssoCode,
							state: state.oAuthState,
							registrationToken: state.registrationToken,
						}),
					});

					const {
						result: {
							userToken = {},
							cognitoToken = {},
							cognitoRefreshToken = {},
							userId,
							ssoCode,
							ssoSubId,
							invitationData,
							registrationTokenData,
						} = {}, error = null,
					} = await response.json();

					if (error) throw new Error(error.code);

					if (userId) {
						// User already has account, proceed to login
						dispatch("setChamaileonCookies", { userToken, cognitoToken, cognitoRefreshToken }, { root: true });

						router.push({ path: "/" }).catch(() => {});
					} else {
						// Invitation params were extracted successfully, it was an invitation
						// as a final step, the user can set its userName and might set a workspace

						if (invitationData?.invitationHash) {
							commit("SET_INVITATION_HASH", invitationData.invitationHash);
							commit("SET_INVITATION_DATA", invitationData);
							commit("SET_USER_WAS_INVITED", true);

							commit("SET_SSO_SUB_ID", ssoSubId);
							commit("SET_REGISTRATION_EMAIL_CODE", ssoCode);
							commit("SET_REGISTRATION_EMAIL_CONFIRMATION", true);

							dispatch("setChamaileonCookies", { cognitoToken, cognitoRefreshToken }, { root: true });
						}

						if (registrationTokenData?.token) {
							commit("SET_SSO_SUB_ID", ssoSubId);
							commit("SET_REGISTRATION_EMAIL_CODE", ssoCode);
							commit("SET_REGISTRATION_EMAIL_CONFIRMATION", true);

							dispatch("setChamaileonCookies", { cognitoToken, cognitoRefreshToken }, { root: true });
						}
						router.push({ path: "/register/account" }).catch(() => {});
					}
				} catch (error) {
					const translationExists = i18n.te(`ssoGetTokensForRegistration.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";

					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`ssoGetTokensForRegistration.errorMessages.${code}`),
						target: {
							uid: "ssoRegistrationError",
							type: "flagToast",
						},
					}, { root: true });
					setTimeout(() => {
						router.push({ path: "/" }).catch(() => {});
					}, 5000);
				}
			},
			async confirmAccountRegistration({ state, dispatch, getters }) {
				try {
					if (state.userWasInvited && !getters.allInvitationsDecisionsAreMade) {
						throw new Error("pendingInvitationNotDecided");
					}
					if (state.registrationEmailCode && state.registrationEmailConfirmation && !state.inlineErrors.userNameNotValidError) {
						const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
						const confirmAccountRequest = await fetch(`${globalConfig.backends.users}/register/${state.registrationEmailCode}/account`, {
							method: "POST",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify({
								displayName: state.name,
								userName: state.userName,
								password: state.password,
								phoneNumber: state.phoneNumber,
								workspaceName: state.workspaceName,
								invitationData: state.invitationData,
							}),
						});

						const { result: {
							email = "",
							userId = "",
							userToken = {},
							cognitoToken = {},
							cognitoRefreshToken = {},
						} = {}, error = null } = await confirmAccountRequest.json();

						if (error) throw new Error(error.code);

						this._vm.$chamaileon.pushToGtmDataLayer({
							event: "accountCreated",
							phoneNumber: state.phoneNumber,
							userId,
							userEmail: email,
						});

						if (userToken?.name && userToken?.token) {
							dispatch("setChamaileonCookies", { userToken }, { root: true });
							// set cognitoTokens for userNamePasswordRegistration.
							if (cognitoToken && cognitoRefreshToken) {
								dispatch("setChamaileonCookies", { cognitoToken, cognitoRefreshToken }, { root: true });
							}

							router.push({ path: "/" }).catch(() => {});
							return;
						} else {
							throw new Error("userTokensNotRetrieved");
						}
					}
				} catch (error) {
					const translationExists = i18n.te(`confirmAccountRegistration.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";

					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`confirmAccountRegistration.errorMessages.${code}`),
						target: {
							uid: "confirmAccountRegistrationError",
							type: "flagToast",
						},
					}, { root: true });
				}
			},
		},
	};
}
